import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { loginRequest } from '@/auth/authConfig';
import { Toaster } from '@/components/ui/sonner';
import { RolesProvider } from '@/contexts/RolesContext';
import { ProvisioningProvider } from '@/contexts/ProvisioningContext';
import { UserProvider } from '@/contexts/UserContext';
import { ClientProvider } from '@/contexts/ClientContext';
import { OrgsProvider } from '@/contexts/OrgsContext';
import { AgreementProvider } from '@/contexts/AgreementContext';
import { OnboardingProvider } from '@/contexts/OnboardingContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { ErrorProvider } from '@/contexts/ErrorContext';
import { SystemNoticesProvider } from '@/contexts/SystemNoticesContext';
import { FeatureFlagsProvider } from '@/contexts/FeatureFlagsContext';
import { ThemeProvider } from '@/components/theme-provider';
import DashboardLayout from '@/layouts/DashboardLayout';
import DashboardPage from '@/pages/DashboardPage';
import ProfilesPage from '@/pages/ProfilesPage';
import ProfileDetailPage from '@/pages/ProfileDetailPage';
import SettingsPage from '@/pages/SettingsPage';
import OrganizationMembersPage from '@/pages/OrganizationMembersPage';
import BillingPage from '@/pages/BillingPage';
import LandingPage from '@/pages/LandingPage';
import AgentsPage from '@/pages/AgentsPage';
import AgentDetailPage from '@/pages/AgentDetailPage';
import AllRunsPage from '@/pages/AllRunsPage';
import SupportPage from '@/pages/SupportPage';
import NewSupportTicketPage from '@/pages/support/NewSupportTicketPage';
import SupportTicketDetailPage from '@/pages/support/SupportTicketDetailPage';
import CreateProfileStep from '@/pages/profiles/wizard/CreateProfileStep';
import AddSourceStep from '@/pages/profiles/wizard/AddSourceStep';
import AddTargetStep from '@/pages/profiles/wizard/AddTargetStep';
import SelectTablesStep from '@/pages/profiles/wizard/SelectTablesStep';
import ConfigureQuietTimeStep from '@/pages/profiles/wizard/ConfigureQuietTimeStep';
import ConfigureDeleteModeStep from '@/pages/profiles/wizard/ConfigureDeleteModeStep';
import RunInitialSyncStep from '@/pages/profiles/wizard/RunInitialSyncStep';

/**
 * Main application component with authentication flow
 */
function App() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  /**
   * Handle user sign-in
   */
  const handleLogin = () => {
    void instance.loginRedirect(loginRequest);
  };

  /**
   * Handle user sign-up - forces account creation flow
   */
  const handleSignUp = () => {
    void instance.loginRedirect({
      ...loginRequest,
      prompt: 'create', // Force the sign-up/create account flow
    });
  };

  /**
   * Auto-trigger sign-in or sign-up when arriving via query parameter
   * from the primary website (e.g. ?action=signin or ?action=signup)
   */
  useEffect(() => {
    if (isAuthenticated) return;

    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');

    if (action === 'signin') {
      handleLogin();
    } else if (action === 'signup') {
      handleSignUp();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <ThemeProvider storageKey="vite-ui-theme">
        <LanguageProvider>
          <ErrorProvider>
            <LandingPage onSignIn={handleLogin} onSignUp={handleSignUp} />
          </ErrorProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider storageKey="vite-ui-theme">
        <LanguageProvider>
          <ErrorProvider>
          <ClientProvider>
            <ProvisioningProvider>
              <UserProvider>
                <OrgsProvider>
                  <AgreementProvider>
                    <RolesProvider>
                      <FeatureFlagsProvider>
                      <SystemNoticesProvider>
                        <OnboardingProvider>
                          <BrowserRouter>
                            <Toaster position="top-right" />
                            <Routes>
                              {/* Redirect root to dashboard */}
                              <Route path="/" element={<Navigate to="/dashboard" replace />} />

                              {/* Dashboard layout with nested routes */}
                              <Route path="/dashboard" element={<DashboardLayout />}>
                                <Route index element={<DashboardPage />} />
                                <Route path="orgs/:orgId/profiles" element={<ProfilesPage />} />
                                {/* Profile wizard routes */}
                                <Route
                                  path="orgs/:orgId/profiles/wizard/profile"
                                  element={<CreateProfileStep />}
                                />
                                <Route
                                  path="orgs/:orgId/profiles/:profileId/setup/profile"
                                  element={<CreateProfileStep />}
                                />
                                <Route
                                  path="orgs/:orgId/profiles/:profileId/setup/source"
                                  element={<AddSourceStep />}
                                />
                                <Route
                                  path="orgs/:orgId/profiles/:profileId/setup/target"
                                  element={<AddTargetStep />}
                                />
                                <Route
                                  path="orgs/:orgId/profiles/:profileId/setup/tables"
                                  element={<SelectTablesStep />}
                                />
                                <Route
                                  path="orgs/:orgId/profiles/:profileId/setup/quiet-time"
                                  element={<ConfigureQuietTimeStep />}
                                />
                                <Route
                                  path="orgs/:orgId/profiles/:profileId/setup/delete-mode"
                                  element={<ConfigureDeleteModeStep />}
                                />
                                <Route
                                  path="orgs/:orgId/profiles/:profileId/setup/initial-sync"
                                  element={<RunInitialSyncStep />}
                                />
                                {/* Profile detail routes */}
                                <Route path="orgs/:orgId/profiles/:profileId" element={<ProfileDetailPage />} />
                                <Route path="orgs/:orgId/agents" element={<AgentsPage />} />
                                <Route path="orgs/:orgId/agents/:agentId" element={<AgentDetailPage />} />
                                <Route path="orgs/:orgId/runs" element={<AllRunsPage />} />
                                <Route path="orgs/:orgId/members" element={<OrganizationMembersPage />} />
                                <Route path="orgs/:orgId/billing" element={<BillingPage />} />
                                <Route path="orgs/:orgId/support" element={<SupportPage />} />
                                <Route path="orgs/:orgId/support/tickets/new" element={<NewSupportTicketPage />} />
                                <Route
                                  path="orgs/:orgId/support/tickets/:ticketId"
                                  element={<SupportTicketDetailPage />}
                                />
                                <Route path="settings" element={<SettingsPage />} />
                                <Route
                                  path="routes"
                                  element={
                                    <div className="text-muted-foreground">Routes page coming soon...</div>
                                  }
                                />
                                <Route
                                  path="logs"
                                  element={<div className="text-muted-foreground">Logs page coming soon...</div>}
                                />
                              </Route>

                              {/* Catch all - redirect to dashboard */}
                              <Route path="*" element={<Navigate to="/dashboard" replace />} />
                            </Routes>
                          </BrowserRouter>
                        </OnboardingProvider>
                      </SystemNoticesProvider>
                      </FeatureFlagsProvider>
                    </RolesProvider>
                  </AgreementProvider>
                </OrgsProvider>
              </UserProvider>
            </ProvisioningProvider>
          </ClientProvider>
          </ErrorProvider>
        </LanguageProvider>
  </ThemeProvider>
  );
}

export default App;
